import React, { useEffect } from 'react';
import {
  Button,
  Drawer,
  Form,
  Input,
  Radio,
  Switch,
  DatePicker,
  message,
  InputNumber,
} from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import {
  REQUEST_LOADING,
  SUCCESS_CREATE,
  SUCCESS_UPDATE,
} from 'constants/alerts';
import { ServicesService } from 'services/ServicesService';
import { requiredRule } from 'utils/forms/validationRules';
import { CONTENT_SPAN, LABEL_SPAN } from 'constants/table';
import dayjs from 'dayjs';

function PromoForm({ isActive, onClose, onSuccess, initialValues }) {
  const [form] = Form.useForm();
  const isEditing = initialValues !== null;

  useEffect(() => {
    if (initialValues) {
      const {
        company,
        type,
        count,
        percentage,
        isActive,
        start_date,
        end_date,
      } = initialValues;

      form.setFieldsValue({
        company,
        type,
        count,
        percentage,
        isActive,
        date: [dayjs(start_date), dayjs(end_date)],
      });
    } else {
      form.resetFields();
    }
  }, [initialValues]);

  const handleSubmit = (values) => {
    const [start, end] = values.date;

    const payload = {
      ...values,
      start_date: start.format('YYYY-MM-DD'),
      end_date: end.format('YYYY-MM-DD'),
    };

    delete payload.date;

    if (isEditing) {
      handleUpdate(payload, initialValues?.group);
    } else {
      handleCreate(payload);
    }
  };

  const handleCreate = async (data) => {
    const hide = message.loading(REQUEST_LOADING);

    try {
      const isCreated = await ServicesService.createPromo(data);

      if (isCreated) {
        message.success(SUCCESS_CREATE);
        handleConfirm();
      }
    } catch (err) {
      message.error('Не удалось создать промо');
    } finally {
      hide();
    }
  };

  const handleUpdate = async (data, group) => {
    const hide = message.loading(REQUEST_LOADING);

    try {
      const isUpdated = await ServicesService.updatePromo(data, group);

      if (isUpdated) {
        message.success(SUCCESS_UPDATE);
        handleConfirm();
      }
    } catch (err) {
      message.error('Не удалось отредактировать промо');
    } finally {
      hide();
    }
  };

  const handleConfirm = () => {
    onClose();
    onSuccess();
    form.resetFields();
  };

  return (
    <Drawer
      title={isEditing ? 'Редактирование промо' : 'Создание промо'}
      width="50%"
      onClose={onClose}
      visible={isActive}
      getContainer={false}
    >
      <Form
        form={form}
        initialValues={{ type: 'unique', isActive: true }}
        onFinish={handleSubmit}
        layout="horizontal"
        labelCol={{
          span: LABEL_SPAN,
        }}
        wrapperCol={{ span: CONTENT_SPAN }}
        labelAlign="right"
      >
        <Form.Item name="company" label="Компания" rules={[requiredRule]}>
          <Input placeholder="Введите наименование компании" allowClear />
        </Form.Item>

        <Form.Item name="type" label="Тип">
          <Radio.Group>
            <Radio value="unique">Уникальный</Radio>
            <Radio value="universal">Универсальный</Radio>
          </Radio.Group>
        </Form.Item>

        <Form.Item name="percentage" label="Скидка" rules={[requiredRule]}>
          <InputNumber
            addonAfter="%"
            controls={false}
            placeholder="Введите скидку"
            style={{ width: '100%' }}
          />
        </Form.Item>

        <Form.Item name="count" label="Лимит" rules={[requiredRule]}>
          <InputNumber
            controls={false}
            placeholder="Лимит, раз на использование"
            style={{ width: '100%' }}
          />
        </Form.Item>

        <Form.Item name="date" label="Дата" rules={[requiredRule]}>
          <DatePicker.RangePicker
            format="DD.MM.YYYY"
            allowClear
            style={{ width: '100%' }}
          />
        </Form.Item>

        <Form.Item name="isActive" label="Активный" valuePropName="checked">
          <Switch
            checkedChildren={<CheckOutlined />}
            unCheckedChildren={<CloseOutlined />}
          />
        </Form.Item>

        <Form.Item wrapperCol={{ offset: CONTENT_SPAN }}>
          <Button type="primary" htmlType="submit">
            Сохранить
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
}

export { PromoForm };
